import { useEffect, useState, useCallback, useMemo } from 'react'
import { useJournal } from '@/hooks/useJournal'
import {
  BookOpen, Plus, Search, X, Star, Tag, BarChart2,
  RefreshCw, Trash2, Edit3, MessageSquare, Lightbulb,
  Eye, ClipboardList, TrendingUp,
} from 'lucide-react'
import { formatDistanceToNow, format } from 'date-fns'
import { clsx } from 'clsx'
import PageHeader from '@/components/ui/PageHeader'
import {
  PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis,
  Tooltip, ResponsiveContainer, CartesianGrid,
} from 'recharts'
import type { JournalEntryType, JournalEmotion } from '@/lib/types'

// ── Constants ─────────────────────────────────────────────────────────────

const ENTRY_TYPES: { id: JournalEntryType | 'all'; label: string; icon: typeof BookOpen }[] = [
  { id: 'all',                 label: 'All',          icon: BookOpen },
  { id: 'trade_note',          label: 'Trade Notes',  icon: ClipboardList },
  { id: 'market_observation',  label: 'Observations', icon: Eye },
  { id: 'lesson',              label: 'Lessons',      icon: Lightbulb },
  { id: 'idea',                label: 'Ideas',        icon: TrendingUp },
  { id: 'review',              label: 'Reviews',      icon: MessageSquare },
]

const EMOTIONS: { value: JournalEmotion; label: string; color: string; bg: string }[] = [
  { value: 'confident', label: 'Confident', color: 'text-emerald',    bg: 'bg-emerald/10 border-emerald/20' },
  { value: 'nervous',   label: 'Nervous',   color: 'text-amber',     bg: 'bg-amber/10 border-amber/20' },
  { value: 'greedy',    label: 'Greedy',     color: 'text-rose-light', bg: 'bg-rose/10 border-rose/20' },
  { value: 'fearful',   label: 'Fearful',    color: 'text-purple-light', bg: 'bg-purple/10 border-purple/20' },
  { value: 'neutral',   label: 'Neutral',    color: 'text-text-secondary', bg: 'bg-surface-card border-border' },
]

const EMOTION_COLORS: Record<string, string> = {
  confident: '#10b981',
  nervous: '#f59e0b',
  greedy: '#f43f5e',
  fearful: '#a78bfa',
  neutral: '#6b7280',
}

const TYPE_LABELS: Record<string, string> = {
  trade_note: 'Trade Note',
  market_observation: 'Observation',
  lesson: 'Lesson',
  idea: 'Idea',
  review: 'Review',
}

function Stars({ rating, size = 14 }: { rating: number; size?: number }) {
  return (
    <div className="flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map(i => (
        <Star
          key={i}
          size={size}
          className={i <= rating ? 'text-amber fill-amber' : 'text-text-muted'}
        />
      ))}
    </div>
  )
}

function EmotionBadge({ emotion }: { emotion: string }) {
  const e = EMOTIONS.find(em => em.value === emotion) ?? EMOTIONS[4]
  return (
    <span className={clsx('inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs border', e.bg, e.color)}>
      {e.label}
    </span>
  )
}

export default function JournalPage() {
  const {
    entries, tags, analytics, loading, loadingAnalytics,
    fetchEntries, fetchTags, fetchAnalytics, search,
    create, remove,
  } = useJournal()

  const [activeType, setActiveType] = useState<JournalEntryType | 'all'>('all')
  const [activeTag, setActiveTag] = useState<string | null>(null)
  const [searchQuery, setSearchQuery] = useState('')
  const [showModal, setShowModal] = useState(false)
  const [showAnalytics, setShowAnalytics] = useState(false)

  // Form state
  const [formTitle, setFormTitle] = useState('')
  const [formBody, setFormBody] = useState('')
  const [formTicker, setFormTicker] = useState('')
  const [formAction, setFormAction] = useState('')
  const [formType, setFormType] = useState<JournalEntryType>('trade_note')
  const [formEmotion, setFormEmotion] = useState<JournalEmotion>('neutral')
  const [formTags, setFormTags] = useState('')
  const [formLesson, setFormLesson] = useState('')
  const [formRating, setFormRating] = useState(3)
  const [formTradeId, setFormTradeId] = useState('')

  useEffect(() => {
    fetchEntries()
    fetchTags()
    fetchAnalytics()
  }, []) // eslint-disable-line

  const refreshAll = useCallback(() => {
    fetchEntries()
    fetchTags()
    fetchAnalytics()
  }, [fetchEntries, fetchTags, fetchAnalytics])

  const handleSearch = useCallback(() => {
    if (searchQuery.trim()) {
      search(searchQuery.trim())
    } else {
      fetchEntries()
    }
  }, [searchQuery, search, fetchEntries])

  const handleCreate = useCallback(async () => {
    if (!formTitle.trim()) return
    await create({
      title: formTitle.trim(),
      body: formBody,
      ticker: formTicker.toUpperCase(),
      action: formAction.toUpperCase(),
      entry_type: formType,
      emotion: formEmotion,
      tags: formTags.split(',').map(t => t.trim()).filter(Boolean),
      lesson_learned: formLesson || null,
      rating: formRating,
      trade_id: formTradeId ? parseInt(formTradeId) : null,
    })
    setShowModal(false)
    setFormTitle(''); setFormBody(''); setFormTicker(''); setFormAction('')
    setFormType('trade_note'); setFormEmotion('neutral'); setFormTags('')
    setFormLesson(''); setFormRating(3); setFormTradeId('')
    fetchAnalytics()
  }, [formTitle, formBody, formTicker, formAction, formType, formEmotion, formTags, formLesson, formRating, formTradeId, create, fetchAnalytics])

  const handleDelete = useCallback(async (id: number) => {
    if (!confirm('Delete this journal entry?')) return
    await remove(id)
    fetchAnalytics()
  }, [remove, fetchAnalytics])

  // Filtered entries
  const filtered = useMemo(() => {
    let result = entries
    if (activeType !== 'all') {
      result = result.filter(e => e.entry_type === activeType)
    }
    if (activeTag) {
      result = result.filter(e => e.tags.includes(activeTag))
    }
    return result
  }, [entries, activeType, activeTag])

  return (
    <div className="space-y-6 animate-fade-in">
      <PageHeader
        title="Trade Journal"
        icon={BookOpen}
        iconClassName="text-text-primary"
        subtitle="Track your trades, emotions, and lessons learned"
        actions={
          <>
            <button
              onClick={() => setShowAnalytics(!showAnalytics)}
              className={clsx('btn-secondary gap-1.5 text-xs', showAnalytics && 'bg-blue/10 text-blue-light')}
            >
              <BarChart2 size={13} aria-hidden /> Analytics
            </button>
            <button onClick={refreshAll} className="btn-secondary gap-1.5 text-xs">
              <RefreshCw size={13} aria-hidden /> Refresh
            </button>
            <button onClick={() => setShowModal(true)} className="btn-primary gap-1.5 text-xs">
              <Plus size={13} aria-hidden /> New Entry
            </button>
          </>
        }
      />

      {/* Analytics panel */}
      {showAnalytics && analytics && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {/* Emotion distribution pie */}
          <div className="card p-4">
            <h3 className="panel-title mb-3">Emotion Distribution</h3>
            {analytics.entries_by_emotion.length > 0 ? (
              <ResponsiveContainer width="100%" height={200}>
                <PieChart>
                  <Pie
                    data={analytics.entries_by_emotion}
                    dataKey="count"
                    nameKey="emotion"
                    cx="50%" cy="50%"
                    outerRadius={70}
                    label={({ emotion, count }) => `${emotion} (${count})`}
                  >
                    {analytics.entries_by_emotion.map((e, i) => (
                      <Cell key={i} fill={EMOTION_COLORS[e.emotion] || '#6b7280'} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <p className="text-sm text-text-muted">No data yet</p>
            )}
          </div>

          {/* Rating by tag */}
          <div className="card p-4">
            <h3 className="panel-title mb-3">Average Rating by Tag</h3>
            {analytics.avg_rating_by_tag.length > 0 ? (
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={analytics.avg_rating_by_tag.slice(0, 8)}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                  <XAxis dataKey="tag" tick={{ fill: '#9ca3af', fontSize: 11 }} />
                  <YAxis domain={[0, 5]} tick={{ fill: '#9ca3af', fontSize: 11 }} />
                  <Tooltip
                    contentStyle={{ background: '#1e1e2e', border: '1px solid #333', borderRadius: 8, color: 'rgb(var(--text-primary))'}}
                    labelStyle={{ color: '#e2e8f0' }}
                  />
                  <Bar dataKey="avg_rating" fill="#3b82f6" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <p className="text-sm text-text-muted">No data yet</p>
            )}
          </div>

          {/* Summary stats */}
          <div className="card p-4 space-y-3">
            <h3 className="panel-title">Journal Summary</h3>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <p className="text-xs text-text-muted">Total Entries</p>
                <p className="text-lg font-bold text-text-primary">{analytics.total_entries}</p>
              </div>
              <div>
                <p className="text-xs text-text-muted">Avg Rating</p>
                <div className="flex items-center gap-1">
                  <p className="text-lg font-bold text-text-primary">{analytics.avg_rating}</p>
                  <Star size={14} className="text-amber fill-amber" />
                </div>
              </div>
            </div>
            {analytics.lessons_learned.length > 0 && (
              <div>
                <p className="text-xs text-text-muted mb-1">Recent Lessons</p>
                {analytics.lessons_learned.slice(0, 3).map((l, i) => (
                  <p key={i} className="text-xs text-text-secondary truncate">
                    {l.lesson}
                  </p>
                ))}
              </div>
            )}
            {analytics.common_mistakes.length > 0 && (
              <div>
                <p className="text-xs text-text-muted mb-1">Common Mistakes</p>
                {analytics.common_mistakes.slice(0, 2).map((m, i) => (
                  <p key={i} className="text-xs text-rose-light truncate">
                    {m.title}
                  </p>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Search + filters */}
      <div className="flex flex-col sm:flex-row gap-3">
        {/* Search */}
        <div className="relative flex-1">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-text-muted" />
          <input
            type="text"
            placeholder="Search journal..."
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && handleSearch()}
            className="input pl-9 w-full text-sm"
          />
          {searchQuery && (
            <button
              onClick={() => { setSearchQuery(''); fetchEntries() }}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-text-muted hover:text-text-primary"
            >
              <X size={14} />
            </button>
          )}
        </div>
      </div>

      {/* Type tabs */}
      <div className="flex gap-1 overflow-x-auto pb-1">
        {ENTRY_TYPES.map(({ id, label, icon: Icon }) => (
          <button
            key={id}
            onClick={() => setActiveType(id)}
            className={clsx(
              'flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs whitespace-nowrap transition-colors',
              activeType === id
                ? 'bg-blue/10 text-blue-light font-medium'
                : 'text-text-secondary hover:bg-surface-card hover:text-text-primary',
            )}
          >
            <Icon size={13} aria-hidden />
            {label}
          </button>
        ))}
      </div>

      {/* Tag filter */}
      {tags.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {activeTag && (
            <button
              onClick={() => setActiveTag(null)}
              className="flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-blue/10 text-blue-light border border-blue/20"
            >
              <X size={10} /> Clear
            </button>
          )}
          {tags.slice(0, 20).map(t => (
            <button
              key={t.tag}
              onClick={() => setActiveTag(activeTag === t.tag ? null : t.tag)}
              className={clsx(
                'flex items-center gap-1 px-2 py-0.5 rounded-full text-xs border transition-colors',
                activeTag === t.tag
                  ? 'bg-blue/10 text-blue-light border-blue/20'
                  : 'bg-surface-card text-text-secondary border-border hover:text-text-primary',
              )}
            >
              <Tag size={10} aria-hidden />
              {t.tag}
              <span className="text-text-muted">({t.count})</span>
            </button>
          ))}
        </div>
      )}

      {/* Journal feed */}
      {loading ? (
        <div className="space-y-3">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="skeleton h-28 rounded-xl" />
          ))}
        </div>
      ) : filtered.length === 0 ? (
        <div className="card p-8 text-center">
          <BookOpen size={32} className="mx-auto mb-3 text-text-muted" />
          <p className="text-text-secondary">No journal entries yet</p>
          <p className="text-sm text-text-muted">Click "New Entry" to start your trade journal</p>
        </div>
      ) : (
        <div className="space-y-3 stagger-children">
          {filtered.map(entry => (
            <div key={entry.id} className="card-hover p-4">
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  {/* Header row */}
                  <div className="flex items-center gap-2 flex-wrap mb-1">
                    <span className="text-xs text-text-muted bg-surface-card px-1.5 py-0.5 rounded">
                      {TYPE_LABELS[entry.entry_type] || entry.entry_type}
                    </span>
                    <EmotionBadge emotion={entry.emotion} />
                    <Stars rating={entry.rating} size={12} />
                    {entry.ticker && (
                      <span className="text-xs font-mono font-semibold text-blue-light">
                        {entry.ticker} {entry.action && `(${entry.action})`}
                      </span>
                    )}
                  </div>

                  {/* Title */}
                  <h3 className="text-sm font-semibold text-text-primary mb-1">{entry.title}</h3>

                  {/* Body preview */}
                  {entry.body && (
                    <p className="text-xs text-text-secondary line-clamp-2 mb-2">
                      {entry.body.replace(/[*#_`]/g, '').slice(0, 200)}
                    </p>
                  )}

                  {/* Lesson learned */}
                  {entry.lesson_learned && (
                    <div className="bg-amber/5 border border-amber/10 rounded-md px-2 py-1 mb-2">
                      <p className="text-xs text-amber flex items-center gap-1">
                        <Lightbulb size={11} /> {entry.lesson_learned}
                      </p>
                    </div>
                  )}

                  {/* Tags + date */}
                  <div className="flex items-center gap-2 flex-wrap">
                    {entry.tags.map(tag => (
                      <button
                        key={tag}
                        onClick={() => setActiveTag(tag)}
                        className="text-xs text-text-muted hover:text-blue-light transition-colors"
                      >
                        #{tag}
                      </button>
                    ))}
                    <span className="text-xs text-text-muted ml-auto">
                      {(() => {
                        try { return formatDistanceToNow(new Date(entry.created_at), { addSuffix: true }) }
                        catch { return entry.created_at?.slice(0, 10) || '' }
                      })()}
                    </span>
                  </div>
                </div>

                {/* Delete button */}
                <button
                  onClick={() => handleDelete(entry.id)}
                  className="p-1.5 rounded-md text-text-muted hover:text-rose-light hover:bg-rose/10 transition-colors shrink-0"
                  title="Delete entry"
                >
                  <Trash2 size={14} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* New Entry Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
          <div className="card w-full max-w-lg max-h-[90vh] overflow-y-auto p-5">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-base font-bold text-text-primary flex items-center gap-2">
                <Edit3 size={16} aria-hidden /> New Journal Entry
              </h2>
              <button onClick={() => setShowModal(false)} className="p-1 text-text-muted hover:text-text-primary">
                <X size={18} />
              </button>
            </div>

            <div className="space-y-3">
              {/* Title */}
              <div>
                <label className="text-xs text-text-secondary mb-1 block">Title *</label>
                <input
                  type="text" placeholder="e.g. NVDA momentum entry"
                  value={formTitle} onChange={e => setFormTitle(e.target.value)}
                  className="input w-full text-sm"
                />
              </div>

              {/* Type + Emotion row */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-text-secondary mb-1 block">Entry Type</label>
                  <select
                    value={formType} onChange={e => setFormType(e.target.value as JournalEntryType)}
                    className="input w-full text-sm"
                  >
                    <option value="trade_note">Trade Note</option>
                    <option value="market_observation">Market Observation</option>
                    <option value="lesson">Lesson</option>
                    <option value="idea">Idea</option>
                    <option value="review">Review</option>
                  </select>
                </div>
                <div>
                  <label className="text-xs text-text-secondary mb-1 block">Emotion</label>
                  <select
                    value={formEmotion} onChange={e => setFormEmotion(e.target.value as JournalEmotion)}
                    className="input w-full text-sm"
                  >
                    {EMOTIONS.map(em => (
                      <option key={em.value} value={em.value}>{em.label}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Ticker + Action */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-text-secondary mb-1 block">Ticker</label>
                  <input
                    type="text" placeholder="e.g. AAPL"
                    value={formTicker} onChange={e => setFormTicker(e.target.value)}
                    className="input w-full text-sm"
                  />
                </div>
                <div>
                  <label className="text-xs text-text-secondary mb-1 block">Action</label>
                  <input
                    type="text" placeholder="e.g. LONG, SHORT, BUY"
                    value={formAction} onChange={e => setFormAction(e.target.value)}
                    className="input w-full text-sm"
                  />
                </div>
              </div>

              {/* Body */}
              <div>
                <label className="text-xs text-text-secondary mb-1 block">Body (markdown supported)</label>
                <textarea
                  rows={4} placeholder="Why did you take this trade? What was your thesis?"
                  value={formBody} onChange={e => setFormBody(e.target.value)}
                  className="input w-full text-sm resize-y"
                />
              </div>

              {/* Tags */}
              <div>
                <label className="text-xs text-text-secondary mb-1 block">Tags (comma separated)</label>
                <input
                  type="text" placeholder="e.g. momentum, earnings, mistake"
                  value={formTags} onChange={e => setFormTags(e.target.value)}
                  className="input w-full text-sm"
                />
              </div>

              {/* Lesson learned */}
              <div>
                <label className="text-xs text-text-secondary mb-1 block">Lesson Learned</label>
                <input
                  type="text" placeholder="What did you learn from this?"
                  value={formLesson} onChange={e => setFormLesson(e.target.value)}
                  className="input w-full text-sm"
                />
              </div>

              {/* Rating */}
              <div>
                <label className="text-xs text-text-secondary mb-1 block">Self-Assessment Rating</label>
                <div className="flex items-center gap-1">
                  {[1, 2, 3, 4, 5].map(i => (
                    <button
                      key={i}
                      onClick={() => setFormRating(i)}
                      className="p-0.5"
                    >
                      <Star
                        size={20}
                        className={clsx(
                          'transition-colors',
                          i <= formRating ? 'text-amber fill-amber' : 'text-text-muted hover:text-amber/50',
                        )}
                      />
                    </button>
                  ))}
                  <span className="text-xs text-text-muted ml-2">{formRating}/5</span>
                </div>
              </div>

              {/* Trade ID */}
              <div>
                <label className="text-xs text-text-secondary mb-1 block">Trade ID (optional link)</label>
                <input
                  type="text" placeholder="e.g. 1"
                  value={formTradeId} onChange={e => setFormTradeId(e.target.value)}
                  className="input w-full text-sm"
                />
              </div>

              {/* Submit */}
              <div className="flex justify-end gap-2 pt-2">
                <button onClick={() => setShowModal(false)} className="btn-secondary text-xs">Cancel</button>
                <button
                  onClick={handleCreate}
                  disabled={!formTitle.trim()}
                  className="btn-primary text-xs disabled:opacity-40"
                >
                  Create Entry
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
